import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from './MainLayout';
import { ChevronLeft, Download, Printer, Search, Loader2, Filter, X, RotateCcw, Info, SlidersHorizontal } from 'lucide-react';
import '../styles/dailyAttendanceReport.css';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';
import { Tooltip } from 'antd';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ── Tiny click-to-open popover (no antd) ── */
function CountPopover({ count, title, items, renderItem }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  if (!count) return <span style={{ color: '#cbd5e1', fontSize: '13px' }}>&#8212;</span>;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontWeight: 700, fontSize: '14px', textDecoration: 'underline', padding: '2px 6px', borderRadius: '4px', lineHeight: 1 }}
      >
        {count}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '220px', maxWidth: '280px' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', fontSize: '12px', fontWeight: 700, color: '#475569', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{title}</div>
          <div style={{ maxHeight: '220px', overflowY: 'auto', padding: '6px 0' }}>
            {items.length === 0
              ? <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '13px' }}>None</div>
              : items.map((item, i) => (
                <div key={i} style={{ padding: '7px 14px', fontSize: '13px', color: '#334155', borderBottom: i < items.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                  {renderItem(item)}
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

const COLUMN_TIPS = {
  emp_id: 'Unique employee identifier',
  full_name: 'Full name of the employee',
  total_days: 'Total calendar days in the selected period',
  weekends: 'Saturdays and Sundays within the period',
  company_holidays: 'Official company holidays within the period',
  company_working_days: 'Total Days minus Weekends minus Holidays',
  employee_working_days: 'Days employee was expected to work (per joining date)',
  present_days: 'Days employee actually checked in',
  absentDetails: 'Days employee was absent. Click count to see dates.',
  attendanceRegularization: 'Attendance regularization requests submitted. Click count to see dates.',
  lopStrikes: 'Late check-in strikes contributing to LOP. Click to see dates and times.',
  totalLopDays: 'Total Loss Of Pay days = Absent days + LOP strike days',
};

function Th({ label, style }) {
  return (
    <th style={{ whiteSpace: 'nowrap', border: 'none', padding: '14px 16px', fontSize: '12px', fontWeight: 700, color: '#64748b', letterSpacing: '0.4px', verticalAlign: 'top', ...style }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <span>{label}</span>
      </div>
    </th>
  );
}

const tdBase = { border: 'none', borderBottom: '1px solid #f1f5f9', padding: '13px 16px', verticalAlign: 'middle' };
const tdNum = { ...tdBase, textAlign: 'center', fontSize: '14px', color: '#334155', fontVariantNumeric: 'tabular-nums' };

export default function LopReport() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [monthName, setMonthName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const today = new Date();
  const [filterType, setFilterType] = useState('currentMonth');
  const [customStartDate, setCustomStartDate] = useState(today.toISOString().split('T')[0]);
  const [customEndDate, setCustomEndDate] = useState(today.toISOString().split('T')[0]);
  const [showFilters, setShowFilters] = useState(false);

  const COLUMNS = [
    { key: 'emp_id', label: 'Employee ID' },
    { key: 'full_name', label: 'Employee Name' },
    { key: 'total_days', label: 'Total Days' },
    { key: 'weekends', label: 'Weekends' },
    { key: 'company_holidays', label: 'Holidays' },
    { key: 'company_working_days', label: 'Working Days' },
    { key: 'employee_working_days', label: 'Emp Working Days' },
    { key: 'present_days', label: 'Present' },
    { key: 'absentDetails', label: 'Absent' },
    { key: 'attendanceRegularization', label: 'Regularization' },
    { key: 'lopStrikes', label: 'LOP Strikes' },
    { key: 'totalLopDays', label: 'Total LOP Days' },
  ];

  const [vis, setVis] = useState(COLUMNS.reduce((a, c) => ({ ...a, [c.key]: true }), {}));
  const [showCols, setShowCols] = useState(false);
  const colRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (colRef.current && !colRef.current.contains(e.target)) setShowCols(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggleVis = (k) => setVis(p => ({ ...p, [k]: !p[k] }));
  const toggleSidebar = () => setSidebarOpen(p => !p);

  const fetchReport = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let s, e2;
      if (filterType === 'last7') { const d = new Date(); d.setDate(d.getDate() - 7); s = d.toISOString().split('T')[0]; e2 = today.toISOString().split('T')[0]; }
      else if (filterType === 'last15') { const d = new Date(); d.setDate(d.getDate() - 15); s = d.toISOString().split('T')[0]; e2 = today.toISOString().split('T')[0]; }
      else if (filterType === 'lastMonth') { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1); s = d.toISOString().split('T')[0]; const e3 = new Date(); e3.setDate(0); e2 = e3.toISOString().split('T')[0]; }
      else if (filterType === 'currentMonth') { const d = new Date(); d.setDate(1); s = d.toISOString().split('T')[0]; e2 = today.toISOString().split('T')[0]; }
      else { s = customStartDate; e2 = customEndDate; }

      const sd = new Date(s);
      const res = await fetch(
        API_BASE + '/api/attendance-report/lop-report?month=' + (sd.getMonth() + 1) + '&year=' + sd.getFullYear() + '&start_date=' + s + '&end_date=' + e2,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Failed');
      setReportData(json.data.lop_reports || []);
      setMonthName(json.data.monthName || '');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [token, filterType, customStartDate, customEndDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows = reportData.filter(i =>
    i.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(i.emp_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fmt = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return String(dt.getDate()).padStart(2, '0') + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + dt.getFullYear();
  };

  const handleExportCSV = () => {
    if (!rows.length) return;
    const hdrs = ['Employee ID', 'Employee Name', 'Total Days', 'Weekends', 'Holidays', 'Working Days', 'Emp Wkg Days', 'Present', 'Absent Count', 'Absent Dates', 'Regularization Count', 'Regularization Dates', 'LOP Strikes Count', 'LOP Strike Dates', 'Total LOP Days'];
    const data = rows.map(r => {
      const ab = r.absent_details || r.absentDetails || [];
      const rg = r.attendance_regularization || r.attendanceRegularization || [];
      const st = r.lop_strikes || r.lopStrikes || [];
      return [r.emp_id, r.full_name, r.total_days, r.weekends, r.company_holidays, r.company_working_days, r.employee_working_days, r.present_days,
      ab.length, '"' + ab.map(a => fmt(a.date) + ' (' + a.type + ')').join(', ') + '"',
      rg.length, '"' + rg.map(a => fmt(a.date)).join(', ') + '"',
      st.length, '"' + st.map(l => fmt(l.date) + ' (' + (l.checkin_time || l.checkinTime) + ')').join(', ') + '"',
      r.total_lop_days != null ? r.total_lop_days : (r.totalLopDays != null ? r.totalLopDays : 0)];
    });
    const csv = 'data:text/csv;charset=utf-8,' + [hdrs.join(','), ...data.map(r => r.join(','))].join('\n');
    const a = document.createElement('a');
    a.href = encodeURI(csv);
    a.download = 'LOP_Report_' + monthName.replace(/\s+/g, '_') + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const filterLabel = { last7: 'Last 7 Days', last15: 'Last 15 Days', lastMonth: 'Last Month', currentMonth: 'This Month', custom: 'Custom Date' }[filterType] || 'This Month';
  const visCount = COLUMNS.filter(c => vis[c.key]).length;

  return (
    <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="LOP Report" subtitle="View monthly LOP details">
      <div className="dr-container">

        {/* Filter bar */}
        <section style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', flex: '0 0 220px' }}>
            <Search size={15} color="#94a3b8" />
            <input
              style={{ border: 'none', outline: 'none', padding: '4px 8px', width: '100%', fontSize: '14px', background: 'transparent' }}
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}><X size={14} /></button>}
          </div>

          {/* Period filter */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowFilters(!showFilters)} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#334155', whiteSpace: 'nowrap' }}>
              <Filter size={13} color="#64748b" /> {filterLabel}
            </button>
            {showFilters && (
              <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 200, width: '270px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Quick Filters</span>
                  <button onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={15} /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                  {[{ key: 'last7', label: 'Last 7 Days' }, { key: 'last15', label: 'Last 15 Days' }, { key: 'lastMonth', label: 'Last Month' }, { key: 'currentMonth', label: 'This Month' }].map(f => (
                    <button key={f.key} onClick={() => { setFilterType(f.key); setShowFilters(false); }}
                      style={{
                        padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
                        border: '1px solid ' + (filterType === f.key ? '#3b82f6' : '#e2e8f0'),
                        background: filterType === f.key ? '#eff6ff' : '#fff',
                        color: filterType === f.key ? '#2563eb' : '#64748b'
                      }}
                    >{f.label}</button>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Custom Date Range</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="date" value={customStartDate} onChange={e => { setFilterType('custom'); setCustomStartDate(e.target.value); }} style={{ flex: 1, padding: '7px 8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', outline: 'none' }} />
                    <input type="date" value={customEndDate} onChange={e => { setFilterType('custom'); setCustomEndDate(e.target.value); }} style={{ flex: 1, padding: '7px 8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', outline: 'none' }} />
                  </div>
                </div>
                <div style={{ marginTop: '14px', borderTop: '1px solid #f1f5f9', paddingTop: '14px' }}>
                  <button onClick={() => { setFilterType('currentMonth'); setSearchQuery(''); setShowFilters(false); }}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #fee2e2', borderRadius: '8px', padding: '8px', gap: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#dc2626' }}>
                    <RotateCcw size={13} /> Reset
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', marginLeft: 'auto', gap: '10px', alignItems: 'center' }}>
            {/* Column toggle */}
            <div ref={colRef} style={{ position: 'relative' }}>
              <Tooltip title="Customize Columns" placement="top">
                <button onClick={() => setShowCols(!showCols)} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50px', padding: '8px', cursor: 'pointer', color: '#334155' }}>
                  <SlidersHorizontal size={14} color="#64748b" />
                </button>
              </Tooltip>
              {showCols && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 200, width: '210px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontWeight: 700, fontSize: '11px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', color: '#94a3b8', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Show / Hide Columns</div>
                  {COLUMNS.map(col => (
                    <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', color: '#475569', userSelect: 'none' }}>
                      <input type="checkbox" checked={!!vis[col.key]} onChange={() => toggleVis(col.key)} style={{ margin: 0, cursor: 'pointer', accentColor: '#3b82f6' }} />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Export and Print Buttons */}
            <Tooltip title="Export CSV" placement="top">
              <button style={{ padding: '8px', borderRadius: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={handleExportCSV}>
                <Download size={14} color="#64748b" />
              </button>
            </Tooltip>
            <Tooltip title="Print" placement="top">
              <button style={{ padding: '8px', borderRadius: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => window.print()}>
                <Printer size={14} color="#64748b" />
              </button>
            </Tooltip>
          </div>
        </section>

        {/* Table */}
        <div className="dr-table-section">
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#dc2626', fontSize: '14px' }}>
              <strong>Error:</strong> {error}
            </div>
          )}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {vis.emp_id && <Th label="Employee ID" style={{ minWidth: 130 }} />}
                    {vis.full_name && <Th label="Employee Name" style={{ minWidth: 160 }} />}
                    {vis.total_days && <Th label="Total Days" style={{ minWidth: 100 }} />}
                    {vis.weekends && <Th label="Weekends" style={{ minWidth: 100 }} />}
                    {vis.company_holidays && <Th label="Holidays" style={{ minWidth: 90 }} />}
                    {vis.company_working_days && <Th label="Working Days" style={{ minWidth: 110 }} />}
                    {vis.employee_working_days && <Th label="Emp Wkg Days" style={{ minWidth: 110 }} />}
                    {vis.present_days && <Th label="Present" style={{ minWidth: 90 }} />}
                    {vis.absentDetails && <Th label="Absent" style={{ minWidth: 90 }} />}
                    {vis.attendanceRegularization && <Th label="Regularization" style={{ minWidth: 130 }} />}
                    {vis.lopStrikes && <Th label="LOP Strikes" style={{ minWidth: 110 }} />}
                    {vis.totalLopDays && <Th label="Total LOP Days" style={{ minWidth: 130 }} />}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={visCount} style={{ textAlign: 'center', padding: '48px 16px', color: '#94a3b8', border: 'none' }}>
                      <Loader2 className="dr-spinner" size={22} /> Loading LOP report...
                    </td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={visCount} style={{ textAlign: 'center', padding: '48px 16px', color: '#94a3b8', border: 'none', fontSize: '14px' }}>
                      No records found for the selected period.
                    </td></tr>
                  ) : rows.map((record, index) => {
                    const ab = record.absent_details || record.absentDetails || [];
                    const rg = record.attendance_regularization || record.attendanceRegularization || [];
                    const st = record.lop_strikes || record.lopStrikes || [];
                    const lop = record.total_lop_days != null ? record.total_lop_days : (record.totalLopDays != null ? record.totalLopDays : 0);
                    const bg = index % 2 === 0 ? '#fff' : '#fafafa';
                    return (
                      <tr key={record.emp_id + '-' + index} style={{ background: bg }}>
                        {vis.emp_id && <td style={tdBase}><span style={{ fontWeight: 600, color: '#2563eb', fontSize: '13px' }}>{record.emp_id}</span></td>}
                        {vis.full_name && <td style={tdBase}><span style={{ fontWeight: 500, color: '#1e293b', fontSize: '14px' }}>{record.full_name}</span></td>}
                        {vis.total_days && <td style={tdNum}>{record.total_days}</td>}
                        {vis.weekends && <td style={tdNum}>{record.weekends}</td>}
                        {vis.company_holidays && <td style={tdNum}>{record.company_holidays}</td>}
                        {vis.company_working_days && <td style={tdNum}>{record.company_working_days}</td>}
                        {vis.employee_working_days && <td style={tdNum}>{record.employee_working_days}</td>}
                        {vis.present_days && <td style={tdNum}>{record.present_days}</td>}
                        {vis.absentDetails && (
                          <td style={tdNum}>
                            <CountPopover count={ab.length} title="Absent Dates" items={ab}
                              renderItem={a => <span><strong style={{ color: '#dc2626' }}>{fmt(a.date)}</strong><span style={{ color: '#64748b', marginLeft: 6 }}>{a.type}</span></span>}
                            />
                          </td>
                        )}
                        {vis.attendanceRegularization && (
                          <td style={tdNum}>
                            <CountPopover count={rg.length} title="Regularization Dates" items={rg}
                              renderItem={a => <span><strong style={{ color: '#7c3aed' }}>{fmt(a.date)}</strong>{a.type && <span style={{ color: '#64748b', marginLeft: 6 }}>{a.type}</span>}</span>}
                            />
                          </td>
                        )}
                        {vis.lopStrikes && (
                          <td style={tdNum}>
                            <CountPopover count={st.length} title="LOP Strike Dates" items={st}
                              renderItem={l => <span><strong style={{ color: '#d97706' }}>{fmt(l.date)}</strong><span style={{ color: '#64748b', marginLeft: 6, fontSize: '12px' }}>{l.checkin_time || l.checkinTime}</span></span>}
                            />
                          </td>
                        )}
                        {vis.totalLopDays && (
                          <td style={tdNum}>
                            <span style={{ fontWeight: 700, fontSize: '15px', color: lop > 0 ? '#be123c' : '#64748b' }}>{lop}</span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!loading && rows.length > 0 && (
              <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Showing <strong style={{ color: '#1e293b' }}>{rows.length}</strong> employee{rows.length !== 1 ? 's' : ''}</span>

              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
